import { Component, ElementRef, EventEmitter, HostListener, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Genre } from 'src/app/shared/model/genre';
import { Network } from 'src/app/shared/model/network';
import { GenreService } from 'src/app/shared/service/genre.service';
import { NetworkService } from 'src/app/shared/service/network.service';
import { environment } from 'src/environments/environment';
import { WebsitesUtilsDialogComponent } from '../networks/networks/utils-dialog/websites-utils-dialog/websites-utils-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { NetworkDetails } from 'src/app/shared/model/network-details';
import { NetworkEpg } from 'src/app/shared/model/network-epg-model/network-epg';
import { NetworkEpgService } from 'src/app/shared/service/network-epg.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-brands',
  templateUrl: './brands.component.html',
  styleUrls: ['./brands.component.scss']
})
export class BrandsComponent implements OnInit {
  networks$: Observable<Network[]> = of([]);
  loadingNetworks = false;
  genres: Genre[] = [];
  selectedGenreId: number | null = null;
  bucketNetworksLogo: string;
  bucketGenresLogo: string;
  allNetworks: Network[] = [];
  searchControl = new FormControl('');
  clickedNetworkCode: string | null = null;
  networkDetails: NetworkDetails;
  networkEpgActual: NetworkEpg;
  networkEpg: NetworkEpg[] = [];
  currentLang: string = '';

  activeNetworks: Network[] = [];
  filteredNetworks: Network[] = [];


  private unsubscription$ = new Subject<void>();
  style: any = {};
  activateStyle: any = {
    opacity: '1',
    filter: 'none',
    clicked: false
  };
  deactivateStyle: any = {
    opacity: '1',
    filter: 'none',
    clicked: false
  };

  constructor(
    private networkService: NetworkService,
    private genreService: GenreService,
    private networkEpgService: NetworkEpgService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private elementRef: ElementRef
  ) {
    this.bucketNetworksLogo = environment.bucketUrlImagesNetworks;
    this.bucketGenresLogo = environment.bucketUrlImagesGenresLogo;
    this.networkDetails = new NetworkDetails();
  }

  ngOnInit(): void {
    this.currentLang = this.translateService.currentLang || this.translateService.getDefaultLang();

    this.loadGenres();
    this.loadNetworks();

      this.searchControl.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(searchTerm => this.filterNetworks(searchTerm))
      ).subscribe(filteredNetworks => {
        this.networks$ = of(filteredNetworks.filter(network => network.active));
      });

    this.networkEpgService.getAllNetwork().subscribe(result => {
      this.networkEpg = result;
    });

    this.subscribeToLanguageChanges();
  }

  private subscribeToLanguageChanges(): void {
    this.translateService.onLangChange.subscribe(event => {
      this.currentLang = event.lang;
      if (this.clickedNetworkCode) {
        const selectedNetwork = this.networkEpg.find(epg => epg.code === this.clickedNetworkCode);
        if (selectedNetwork) {
          this.changeNetworkDetails(selectedNetwork, this.currentLang);
        }
      }
    });
  }

  private loadGenres(): void {
    this.genreService.getGenres().subscribe(
      (data) => {
        this.genres = data.map((genre) => ({
          ...genre,
          active: false
        }));
      },
      (error) => {
        console.error("Error al cargar los géneros:", error);
      }
    );
  }

    private loadNetworks(): void {
        this.loadingNetworks = true;
        this.networkService.getNetworks$().pipe(
          takeUntil(this.unsubscription$)
        ).subscribe(networks => {
          this.allNetworks = networks.filter(network => network.active);;
          this.networks$ = of(networks.filter(network => network.active));
          this.loadingNetworks = false;
        });
    }

    filterByGenre(genre: Genre): void {
        if (genre.active) {
            this.genres.forEach((g) => g.active = false);
            this.selectedGenreId = null;
            this.loadNetworks();
        } else {
            this.genres.forEach((g) => g.active = g.id === genre.id);
            this.selectedGenreId = genre.id;
            this.loadingNetworks = true;
            this.networks$ = this.networkService.getNetworksByGenre(this.selectedGenreId).pipe(
                switchMap(networks => {
                    this.loadingNetworks = false;
                    return of(networks.filter(network => network.active));
                })
            );
        }
    }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const clickedElement = event.target as HTMLElement;
    const isClickInside = this.elementRef.nativeElement.contains(clickedElement);
    if (!isClickInside && this.clickedNetworkCode) {
      this.clickedNetworkCode = null;
      this.networkDetails = new NetworkDetails();
    }
  }

  clickNetwork(network: Network) {
    if (this.clickedNetworkCode === network.code) {
      this.clickedNetworkCode = null;
      this.networkDetails = new NetworkDetails();
    } else {
      this.clickedNetworkCode = network.code;
      this.changeNetwork(network);
    }
  }

  changeNetwork(network: Network) {
    const networkEpgActual = this.networkEpg.find(epg => epg.code === network.code);
    if (networkEpgActual) {
      this.networkEpgActual = networkEpgActual;
      this.changeNetworkDetails(networkEpgActual, this.currentLang);
    }
  }

  changeNetworkDetails(networkEpgActual: NetworkEpg | undefined, lang: string) {
    if (!networkEpgActual) return;

    switch (lang) {
      case 'en':
        this.networkDetails.about = networkEpgActual.informationEn;
        this.networkDetails.urls = networkEpgActual.urlsEn;
        this.networkDetails.urlFacebook = networkEpgActual.urlFacebookEn || null;
        this.networkDetails.urlTwitter = networkEpgActual.urlTwitterEn || null;
        break;
      case 'es':
        this.networkDetails.about = networkEpgActual.informationEs;
        this.networkDetails.urls = networkEpgActual.urlsEs;
        this.networkDetails.urlFacebook = networkEpgActual.urlFacebookEs || null;
        this.networkDetails.urlTwitter = networkEpgActual.urlTwitterEs || null;
        break;
      case 'pt':
        this.networkDetails.about = networkEpgActual.informationPt;
        this.networkDetails.urls = networkEpgActual.urlsPt;
        this.networkDetails.urlFacebook = networkEpgActual.urlFacebookPt || null;
        this.networkDetails.urlTwitter = networkEpgActual.urlTwitterPt || null;
        break;
    }
  }


  private filterNetworks(searchTerm: string): Observable<Network[]> {
    if (!searchTerm || searchTerm.trim() === '') {
        return of(this.filteredNetworks);
    }

    const filtered = this.filteredNetworks.filter(network =>
        network.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

        return of(filtered.filter(network => network.active));
    }

  openLink(link: string) {
    if (link) {
      window.open(link, '_blank');
    }
  }

  openFacebook() {
    if (this.networkDetails?.urlFacebook) {
      this.openLink(this.networkDetails.urlFacebook);
    }
  }

  openTwitter() {
    if (this.networkDetails?.urlTwitter) {
      this.openLink(this.networkDetails.urlTwitter);
    }
  }

  openWebsite() {
    if (this.networkDetails?.urls && this.networkDetails.urls.length > 0) {
      this.dialog.open(WebsitesUtilsDialogComponent, {
        data: this.networkDetails.urls,
        minWidth: '350px',
      });
    }
  }

  navigateToNetwork(network: any) {
    this.router.navigate(["/epg/networks"], { state: { network } });
  }

  ngOnDestroy(): void {
    this.unsubscription$.next();
    this.unsubscription$.complete();
  }
}
